import React, { useState, useEffect, useCallback } from 'react'
import { getApiUrl, apiRequest, getAuthHeaders } from '../utils/api';

const BatchCampaignCompletion = ({ campaigns, onComplete, onCancel }) => {
  const [campaignForms, setCampaignForms] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commonFields, setCommonFields] = useState({
    team_member: '',
    product: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Helper functions from CampaignCompletionForm
  const getNested = useCallback((obj, pathArr) => {
    try {
      return pathArr.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
    } catch {
      return undefined;
    }
  }, []);

  const getUsername = (info) => (
    info?.username || info?.userName || info?.name ||
    getNested(info, ['user', 'username']) ||
    getNested(info, ['owner', 'username']) ||
    getNested(info, ['author', 'username']) ||
    getNested(info, ['graphql', 'user', 'username']) ||
    getNested(info, ['profile', 'username']) || ''
  );

  const getFollowers = useCallback((info) => (
    info?.followersCount || info?.followers || info?.followerCount ||
    getNested(info, ['graphql', 'user', 'edge_followed_by', 'count']) ||
    getNested(info, ['user', 'edge_followed_by', 'count']) ||
    getNested(info, ['statistics', 'followers']) ||
    getNested(info, ['counts', 'followers']) ||
    getNested(info, ['edge_followed_by', 'count']) || undefined
  ), [getNested]);

  const getFollowing = (info) => (
    info?.followingCount || info?.following || info?.followsCount ||
    getNested(info, ['graphql', 'user', 'edge_follow', 'count']) ||
    getNested(info, ['user', 'edge_follow', 'count']) ||
    getNested(info, ['statistics', 'following']) ||
    getNested(info, ['counts', 'following']) ||
    getNested(info, ['edge_follow', 'count']) || undefined
  );

  const getPosts = (info) => (
    info?.postsCount || info?.mediaCount || info?.media_count ||
    getNested(info, ['graphql', 'user', 'edge_owner_to_timeline_media', 'count']) ||
    getNested(info, ['user', 'media', 'count']) || undefined
  );

  const getFullName = (info) => (
    info?.fullName || info?.full_name || info?.name ||
    getNested(info, ['graphql', 'user', 'full_name']) ||
    getNested(info, ['user', 'full_name']) || undefined
  );

  const getBio = (info) => (
    info?.bio || info?.biography || info?.description ||
    getNested(info, ['graphql', 'user', 'biography']) || undefined
  );

  const getProfileUrl = (info) => (
    info?.url || info?.profile_url ||
    (getUsername(info) ? `https://www.instagram.com/${getUsername(info)}/` : undefined)
  );

  const buildDisplayProfile = (campaign) => {
    const raw = campaign.influencer_info;
    let influencerInfo = null;
    if (raw) {
      if (typeof raw === 'string') {
        try { influencerInfo = JSON.parse(raw); } catch { influencerInfo = null; }
      } else {
        influencerInfo = raw;
      }
    }

    if (influencerInfo && !influencerInfo.error) return influencerInfo;
    
    const derivedUsername = campaign.influencer_username || campaign.influencer_name || '';
    const profileUrl = derivedUsername ? `https://www.instagram.com/${derivedUsername}/` : undefined;
    return {
      username: derivedUsername,
      fullName: undefined,
      followersCount: undefined,
      followingCount: undefined,
      postsCount: undefined,
      bio: undefined,
      externalUrl: undefined,
      url: profileUrl
    };
  };

  const autoSelectTier = useCallback((displayProfile) => {
    const followers = getFollowers(displayProfile);
    if (!followers) return 'Tier 1';
    const followersNum = parseInt(followers);
    if (Number.isNaN(followersNum)) return 'Tier 1';
    if (followersNum < 10000) return 'Nano';
    if (followersNum < 50000) return 'Micro';
    if (followersNum < 200000) return 'Mid';
    if (followersNum < 1000000) return 'Macro';
    return 'Mega';
  }, [getFollowers]);

  useEffect(() => {
    // Initialize forms with extracted campaign data
    const forms = campaigns.map((campaign, index) => {
      const displayProfile = buildDisplayProfile(campaign);
      const autoTier = autoSelectTier(displayProfile);
      
      return {
        index: index + 1,
        influencer_name: campaign.influencer_name || '',
        influencer_username: campaign.influencer_username || '',
        amount: '',
        views: (campaign.views && campaign.views > 0) ? campaign.views : 0,
        tier: (campaign.tier && campaign.tier !== 'Unknown') ? campaign.tier : autoTier,
        platform: campaign.platform || 'Instagram',
        likes: (campaign.likes && campaign.likes > 0) ? campaign.likes : 0,
        comments: (campaign.comments && campaign.comments > 0) ? campaign.comments : 0,
        reposts: '',
        shares: (campaign.shares && campaign.shares > 0) ? campaign.shares : 0,
        video_url: campaign.video_url || '',
        caption: campaign.caption || '',
        video_thumbnail: campaign.video_thumbnail || '',
        short_code: campaign.short_code || '',
        post_link: campaign.post_link || '',
        influencer_info: campaign.influencer_info || null,
        description: campaign.description || '',
        displayProfile: displayProfile
      };
    });
    setCampaignForms(forms);

    // Fetch team members and products
    const fetchTeamMembers = () => {
      apiRequest(`/team-members`)
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            setTeamMembers(data.team_members);
          }
        })
        .catch(error => console.error('Error fetching team members:', error));
    };

    const fetchProducts = () => {
      apiRequest(`/products`)
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            setProducts(data.products);
          }
        })
        .catch(error => console.error('Error fetching products:', error));
    };

    // Initial fetch
    fetchTeamMembers();
    fetchProducts();

    // Refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTeamMembers();
        fetchProducts();
      }
    };

    // Periodic refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchTeamMembers();
      fetchProducts();
    }, 30000);

    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (e) => {
      if (e.key === 'team_members_updated') {
        fetchTeamMembers();
      }
      if (e.key === 'products_updated') {
        fetchProducts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [campaigns, autoSelectTier]);

  // Fetch profile info for campaigns that don't have it
  useEffect(() => {
    if (campaignForms.length === 0) return;
    
    const fetchMissingProfiles = async () => {
      const updates = await Promise.all(
        campaignForms.map(async (form) => {
          // If we already have influencer_info, skip
          if (form.influencer_info) {
            const raw = form.influencer_info;
            let influencerInfo = null;
            if (typeof raw === 'string') {
              try { influencerInfo = JSON.parse(raw); } catch { influencerInfo = null; }
            } else {
              influencerInfo = raw;
            }
            if (influencerInfo && !influencerInfo.error && getFollowers(influencerInfo)) {
              return null; // Already have valid profile info with followers
            }
          }

          // Try to get username
          const username = form.influencer_username || form.influencer_name || '';
          if (!username || username === 'Unknown') return null;

          try {
            const resp = await fetch(`${getApiUrl()}/profile-info`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: username.replace('@', '').trim() })
            });
            const res = await resp.json();
            const info = res.data || res.profile_info;
            if (res.success && info && !info.error) {
              return { index: form.index, influencer_info: info };
            }
          } catch (error) {
            console.error(`Error fetching profile for ${username}:`, error);
          }
          return null;
        })
      );

      // Update forms with fetched profile info
      const updatesMap = {};
      updates.forEach(update => {
        if (update) {
          updatesMap[update.index] = update.influencer_info;
        }
      });

      if (Object.keys(updatesMap).length > 0) {
        setCampaignForms(prev => prev.map(form => {
          if (updatesMap[form.index]) {
            const displayProfile = buildDisplayProfile({ influencer_info: updatesMap[form.index], influencer_username: form.influencer_username, influencer_name: form.influencer_name });
            const autoTier = autoSelectTier(displayProfile);
            return { 
              ...form, 
              influencer_info: updatesMap[form.index],
              tier: (form.tier === 'Tier 1' || !form.tier || form.tier === 'Unknown') ? autoTier : form.tier,
              displayProfile 
            };
          }
          return form;
        }));
      }
    };

    fetchMissingProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns.length]); // Only run when campaigns prop changes

  const updateCampaignForm = (index, field, value) => {
    setCampaignForms(prev => prev.map((form, i) => 
      i === index ? { ...form, [field]: value } : form
    ));
  };

  const updateCommonField = (field, value) => {
    setCommonFields(prev => ({ ...prev, [field]: value }));
    // Only apply to forms that don't have a value set yet (use as default)
    setCampaignForms(prev => prev.map(form => {
      if (!form[field] || form[field] === '') {
        return { ...form, [field]: value };
      }
      return form;
    }));
  };

  const removeCampaign = (index) => {
    if (window.confirm(`Are you sure you want to remove Campaign ${index}? This will remove it from the batch completion.`)) {
      setCampaignForms(prev => prev.filter(form => form.index !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all forms
    const invalidForms = campaignForms.filter(form => {
      const sharesTrim = (form.shares ?? '').toString().trim();
      const repostsTrim = (form.reposts ?? '').toString().trim();
      const teamMember = form.team_member || commonFields.team_member;
      const product = form.product || commonFields.product;
      return !form.influencer_name || !form.amount || form.views === null || form.views === undefined || 
             sharesTrim === '' || repostsTrim === '' || !teamMember || !product;
    });

    if (invalidForms.length > 0) {
      alert(`Please fill in all required fields (Amount, Views, Shares, Reposts, Team Member, Product) for ${invalidForms.length} campaign(s).`);
      return;
    }

    if (campaignForms.length === 0) {
      alert('No campaigns to complete. Please add at least one campaign.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare campaigns data
      const campaignsData = campaignForms.map(form => ({
        influencer_name: form.influencer_name,
        amount: parseFloat(form.amount),
        views: parseInt(form.views),
        date: form.date || commonFields.date,
        platform: form.platform,
        tier: form.tier,
        likes: parseInt(form.likes || 0),
        comments: parseInt(form.comments || 0),
        reposts: parseInt(form.reposts || 0),
        shares: parseInt(form.shares || 0),
        team_member: form.team_member || commonFields.team_member || 'Manual Entry',
        product: form.product || commonFields.product || 'Unknown',
        description: form.description || '',
        video_url: form.video_url || '',
        caption: form.caption || '',
        video_thumbnail: form.video_thumbnail || '',
        short_code: form.short_code || '',
        post_link: form.post_link || '',
        influencer_info: form.influencer_info
      }));

      const response = await fetch(`${getApiUrl()}/campaigns/complete-multiple`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ campaigns: campaignsData }),
      });

      const result = await response.json();

      if (result.success) {
        alert(`✅ Successfully completed ${result.successful} out of ${result.total} campaigns!`);
        if (onComplete) {
          onComplete(result);
        }
      } else {
        alert('Error: ' + (result.error || 'Failed to complete campaigns'));
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Network error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Complete Multiple Campaigns ({campaignForms.length})</h2>
        <p className="text-gray-600">Add additional details to complete all campaigns</p>
      </div>

      {/* Common Fields - Default Values */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Default Values (Applied to Empty Fields Only)</h3>
        <p className="text-sm text-gray-600 mb-4">
          These values will be used as defaults for campaigns that don't have these fields filled. You can override them for individual campaigns.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Team Member
            </label>
            <select
              value={commonFields.team_member}
              onChange={(e) => updateCommonField('team_member', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select default team member</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.name}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Product
            </label>
            <select
              value={commonFields.product}
              onChange={(e) => updateCommonField('product', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select default product</option>
              {products.map(product => (
                <option key={product.id} value={product.name}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Date
            </label>
            <input
              type="date"
              value={commonFields.date}
              onChange={(e) => updateCommonField('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Campaign Forms */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-6 max-h-[800px] overflow-y-auto pr-2">
          {campaignForms.map((form, index) => {
            const displayProfile = form.displayProfile || buildDisplayProfile(form);
            const profileUrl = getProfileUrl(displayProfile);
            
            // Check if campaign has null/empty data
            const hasEmptyData = !form.influencer_name || !form.post_link || (!form.views && form.views !== 0);
            
            return (
              <div key={index} className={`bg-white rounded-lg shadow-sm border p-6 relative ${hasEmptyData ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
                {/* Campaign Header with Warning and Remove Button */}
                <div className={`mb-6 pb-4 border-b ${hasEmptyData ? 'border-yellow-300' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between mb-2 gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {hasEmptyData && (
                          <div className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs font-medium">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            Incomplete Data
                          </div>
                        )}
                        <h3 className={`text-xl font-bold ${hasEmptyData ? 'text-yellow-900' : 'text-gray-900'}`}>
                          Campaign {form.index}: {form.influencer_name || 'Unknown Influencer'}
                        </h3>
                      </div>
                      {hasEmptyData && (
                        <p className="text-sm text-yellow-700 mt-1">Extraction returned empty/null data. You can remove this campaign or fill it manually.</p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {/* Remove Campaign Button */}
                      <button
                        type="button"
                        onClick={() => removeCampaign(form.index)}
                        className="inline-flex items-center px-2 py-1 border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 rounded-md text-sm font-medium transition-colors"
                        title="Remove this campaign from batch"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Remove
                      </button>
                      {form.post_link && (
                        <button
                          type="button"
                          onClick={() => window.open(form.post_link, '_blank')}
                          className="inline-flex items-center px-3 py-1 border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md text-sm font-medium transition-colors"
                          title="Open reel in a new tab"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Reel
                        </button>
                      )}
                      {profileUrl && (
                        <button
                          type="button"
                          onClick={() => window.open(profileUrl, '_blank')}
                          className="inline-flex items-center px-3 py-1 border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-md text-sm font-medium transition-colors"
                          title="Open profile in a new tab"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A7 7 0 1118.88 7.196M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Profile
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Short Code Display */}
                  {form.short_code && (
                    <p className="text-sm text-gray-500">Short Code: {form.short_code}</p>
                  )}
                </div>

                {/* Influencer Account Details */}
                {(getUsername(displayProfile) || form.influencer_username || form.influencer_name) ? (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <h4 className="text-md font-semibold text-purple-700 mb-2">Influencer Account Details</h4>
                    <div className="text-sm text-gray-700">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <strong>Username:</strong> {getUsername(displayProfile) || form.influencer_username || form.influencer_name || 'N/A'}
                        </div>
                        <div><strong>Full Name:</strong> {getFullName(displayProfile) || 'N/A'}</div>
                        <div className="flex items-center">
                          <strong>Followers:</strong> 
                          <span className={`ml-1 ${getFollowers(displayProfile) ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>
                            {getFollowers(displayProfile) ? getFollowers(displayProfile).toLocaleString() : 'Loading...'}
                          </span>
                          {getFollowers(displayProfile) && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                              {form.tier} Tier
                            </span>
                          )}
                        </div>
                        <div>
                          <strong>Following:</strong> {getFollowing(displayProfile) ? getFollowing(displayProfile).toLocaleString() : 'N/A'}
                        </div>
                        <div>
                          <strong>Posts:</strong> {getPosts(displayProfile) ? getPosts(displayProfile).toLocaleString() : 'N/A'}
                        </div>
                      </div>
                      {getBio(displayProfile) && (
                        <div className="mt-2"><strong>Bio:</strong> {getBio(displayProfile)}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                    <strong>Profile details not available</strong> - You can still complete the campaign and set the tier manually.
                  </div>
                )}

                {/* Financial Information */}
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Amount (₹) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={form.amount}
                        onChange={(e) => updateCampaignForm(index, 'amount', e.target.value)}
                        required
                        min="0"
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Views *
                      </label>
                      <input
                        type="number"
                        value={form.views}
                        onChange={(e) => updateCampaignForm(index, 'views', e.target.value)}
                        required
                        min="0"
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Campaign Details */}
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Campaign Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Team Member *
                      </label>
                      <select
                        value={form.team_member || commonFields.team_member || ''}
                        onChange={(e) => updateCampaignForm(index, 'team_member', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">{commonFields.team_member ? `Default: ${commonFields.team_member}` : 'Select team member'}</option>
                        {teamMembers.map(member => (
                          <option key={member.id} value={member.name}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product *
                      </label>
                      <select
                        value={form.product || commonFields.product || ''}
                        onChange={(e) => updateCampaignForm(index, 'product', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">{commonFields.product ? `Default: ${commonFields.product}` : 'Select product'}</option>
                        {products.map(product => (
                          <option key={product.id} value={product.name}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Platform
                      </label>
                      <select
                        value={form.platform}
                        onChange={(e) => updateCampaignForm(index, 'platform', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Instagram">Instagram</option>
                        <option value="TikTok">TikTok</option>
                        <option value="YouTube">YouTube</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Twitter">Twitter</option>
                        <option value="LinkedIn">LinkedIn</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tier {getFollowers(displayProfile) && (
                          <span className="text-green-600 text-xs font-normal">
                            (Auto-selected based on {getFollowers(displayProfile).toLocaleString()} followers)
                          </span>
                        )}
                      </label>
                      <select
                        value={form.tier}
                        onChange={(e) => updateCampaignForm(index, 'tier', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          getFollowers(displayProfile) ? 'border-green-300 bg-green-50' : 'border-gray-300'
                        }`}
                      >
                        <option value="Nano">Nano (1K-10K)</option>
                        <option value="Micro">Micro (10K-50K)</option>
                        <option value="Mid">Mid (50K-200K)</option>
                        <option value="Macro">Macro (200K-1M)</option>
                        <option value="Mega">Mega (1M+)</option>
                      </select>
                      {getFollowers(displayProfile) && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Tier automatically selected based on follower count
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Engagement Data */}
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Engagement Data
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      (Edit if AI analysis is incorrect)
                    </span>
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Likes ❤️
                      </label>
                      <input
                        type="number"
                        value={form.likes}
                        onChange={(e) => updateCampaignForm(index, 'likes', e.target.value)}
                        min="0"
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Comments 💬
                      </label>
                      <input
                        type="number"
                        value={form.comments}
                        onChange={(e) => updateCampaignForm(index, 'comments', e.target.value)}
                        min="0"
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reshares ↗️ *
                      </label>
                      <input
                        type="number"
                        value={form.reposts}
                        onChange={(e) => updateCampaignForm(index, 'reposts', e.target.value)}
                        min="0"
                        placeholder=""
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Shares 📤 *
                      </label>
                      <input
                        type="number"
                        value={form.shares}
                        onChange={(e) => updateCampaignForm(index, 'shares', e.target.value)}
                        min="0"
                        placeholder=""
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Views 👁️
                      </label>
                      <input
                        type="number"
                        value={form.views}
                        onChange={(e) => updateCampaignForm(index, 'views', e.target.value)}
                        min="0"
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Video Information */}
                <div className="mb-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Video Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video URL 🎥
                      </label>
                      <input
                        type="url"
                        value={form.video_url}
                        onChange={(e) => updateCampaignForm(index, 'video_url', e.target.value)}
                        placeholder="https://example.com/video.mp4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Caption 📝
                      </label>
                      <textarea
                        value={form.caption}
                        onChange={(e) => updateCampaignForm(index, 'caption', e.target.value)}
                        rows={3}
                        placeholder="Enter post caption..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  
                  {/* Video Thumbnail Preview */}
                  {form.video_thumbnail && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video Thumbnail Preview 🖼️
                      </label>
                      <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                        <img 
                          src={form.video_thumbnail} 
                          alt="Video thumbnail" 
                          className="max-w-xs h-auto rounded-md shadow-sm"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 bg-white sticky bottom-0 pb-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              `Complete All Campaigns (${campaignForms.length})`
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BatchCampaignCompletion;
